import { useMemo, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CRITERION_CATEGORIES,
  CriterionCategorySchema,
  type AddCriterionAnswerRequest,
  type CriterionCategory,
  type CriterionConfigDto,
  type TrialCriterionAnswerDto,
  type TrialCriterionDto,
} from '@ctms/contracts';
import { isHTTPError } from 'ky';
import { AlertCircle, X } from 'lucide-react';
import { z } from 'zod';

import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';

export interface CriteriaCatalogProps {
  trialId: number;
  canWrite: boolean;
}

const CATEGORY_LABELS: Record<CriterionCategory, string> = {
  [CRITERION_CATEGORIES.INCLUSION]: 'Włączenia',
  [CRITERION_CATEGORIES.EXCLUSION]: 'Wyłączenia',
};

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

const actionButtonClass =
  'rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800';

const columnHelper = createAppColumnHelper<CriterionConfigDto>();

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

export function formatCategory(value: CriterionCategory): string {
  return CATEGORY_LABELS[value];
}

/** REQ-PAT-019: "M, K" / "M" / "K" — gate 5 forbids the both-false case. */
export function formatGenderScope(
  criterion: Pick<TrialCriterionDto, 'appliesMale' | 'appliesFemale'>,
): string {
  if (criterion.appliesMale && criterion.appliesFemale) {
    return 'M, K';
  }
  if (criterion.appliesMale) {
    return 'M';
  }
  if (criterion.appliesFemale) {
    return 'K';
  }
  return '—';
}

export function formatAnswerState(met: boolean): string {
  return met ? 'spełnione' : 'niespełnione';
}

/**
 * Gate 5 (frontend half): unchecking BOTH genders is rejected in the dialog —
 * the DB CHECK and the API refine are the backstops.
 */
export const CriterionFormSchema = z
  .object({
    content: z.string().trim().min(1, 'Treść kryterium jest wymagana'),
    category: CriterionCategorySchema,
    appliesMale: z.boolean(),
    appliesFemale: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (!value.appliesMale && !value.appliesFemale) {
      ctx.addIssue({
        code: 'custom',
        path: ['appliesMale'],
        message: 'Kryterium musi dotyczyć co najmniej jednej płci',
      });
    }
  });

export type CriterionFormInput = z.infer<typeof CriterionFormSchema>;

const EMPTY_CRITERION_FORM: CriterionFormInput = {
  content: '',
  category: CRITERION_CATEGORIES.INCLUSION,
  appliesMale: true,
  appliesFemale: true,
};

/**
 * Qualification criteria configuration of one trial (issue #30,
 * REQ-PAT-018…020). Read-only without `trials:write`; deletes are guarded by
 * gate 6 (409 while patients have saved answers).
 */
export function CriteriaCatalog({ trialId, canWrite }: CriteriaCatalogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState<TrialCriterionDto | null>(null);
  const [answersCriterion, setAnswersCriterion] = useState<TrialCriterionDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TrialCriterionDto | null>(null);

  const criteriaQuery = useQuery({
    queryKey: trialQueryKeys.criteria(trialId),
    queryFn: () => trialsApi.listCriteria(trialId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const items = criteriaQuery.data?.items ?? [];
  const selectedAnswers =
    items.find((entry) => entry.criterion.id === answersCriterion?.id)?.answers ?? [];

  const columns = useMemo(() => {
    const base = [
      columnHelper.display({
        id: 'content',
        header: 'Treść',
        cell: (info) => info.row.original.criterion.content,
      }),
      columnHelper.display({
        id: 'category',
        header: 'Kategoria',
        cell: (info) => formatCategory(info.row.original.criterion.category),
      }),
      columnHelper.display({
        id: 'gender',
        header: 'Dotyczy płci',
        cell: (info) => formatGenderScope(info.row.original.criterion),
      }),
      columnHelper.display({
        id: 'answers',
        header: 'Zdefiniowane odpowiedzi',
        cell: (info) => {
          const answers = info.row.original.answers;
          return (
            <div>
              <span>{answers.length}</span>
              {answers.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-slate-400">
                  {answers.map((answer) => (
                    <li key={answer.id}>
                      {answer.answer} ({formatAnswerState(answer.met)})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        },
      }),
    ];
    if (canWrite) {
      base.push(
        columnHelper.display({
          id: 'actions',
          header: 'Działania',
          cell: (info) => {
            const { criterion } = info.row.original;
            return (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditingCriterion(criterion);
                    setIsFormOpen(true);
                  }}
                  className={actionButtonClass}
                >
                  Edytuj
                </button>
                <button
                  type="button"
                  onClick={() => setAnswersCriterion(criterion)}
                  className={actionButtonClass}
                >
                  Odpowiedzi
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(criterion)}
                  className={actionButtonClass}
                >
                  Usuń
                </button>
              </div>
            );
          },
        }),
      );
    }
    return columnHelper.columns(base);
  }, [canWrite]);
  const table = useAppTable({ data: items, columns });

  const openAddDialog = (): void => {
    setEditingCriterion(null);
    setIsFormOpen(true);
  };

  const closeForm = (): void => {
    setIsFormOpen(false);
    setEditingCriterion(null);
  };

  const handleFormSaved = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.criteria(trialId) });
    closeForm();
  };

  return (
    <section
      aria-label="Kryteria kwalifikacji"
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-4"
    >
      <h3 className="text-sm font-semibold text-slate-100">Kryteria</h3>
      <p className="mt-1 text-xs text-slate-400">{`Kryteria: ${items.length}`}</p>
      {!canWrite && (
        <p className="mt-1 text-xs text-slate-400">
          Widok tylko do odczytu — edycja kryteriów wymaga uprawnienia do zapisu.
        </p>
      )}

      {criteriaQuery.isLoading ? (
        <p className="py-6 text-sm text-slate-400">Ładowanie kryteriów</p>
      ) : criteriaQuery.isError ? (
        <div role="alert" className="mt-3 text-sm text-rose-300">
          Nie udało się wczytać kryteriów.
          <button
            type="button"
            onClick={() => void criteriaQuery.refetch()}
            className="ml-3 underline"
          >
            Spróbuj ponownie
          </button>
        </div>
      ) : items.length === 0 ? (
        <div>
          <p className="mt-2 text-xs text-slate-400">Brak kryteriów — dodaj pierwsze.</p>
          {canWrite && (
            <button
              type="button"
              onClick={openAddDialog}
              className="mt-4 rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
            >
              Dodaj kryterium
            </button>
          )}
        </div>
      ) : (
        <div>
          <div className="mt-3 overflow-hidden rounded-lg border border-slate-800">
            <table className="w-full text-left text-xs">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} scope="col" className="px-3 py-2 text-slate-400">
                        <table.FlexRender header={header} />
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-800/80">
                    {row.getAllCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 text-slate-200">
                        <table.FlexRender cell={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={openAddDialog}
              className="mt-4 rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
            >
              Dodaj kryterium
            </button>
          )}
        </div>
      )}

      {isFormOpen && (
        <CriterionFormDialog
          trialId={trialId}
          criterion={editingCriterion ?? undefined}
          onClose={closeForm}
          onSaved={handleFormSaved}
        />
      )}
      {answersCriterion && (
        <CriterionAnswersDialog
          trialId={trialId}
          criterion={answersCriterion}
          answers={selectedAnswers}
          onClose={() => setAnswersCriterion(null)}
        />
      )}
      {deleteTarget && (
        <DeleteCriterionDialog
          trialId={trialId}
          criterion={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </section>
  );
}

function CriterionFormDialog({
  trialId,
  criterion,
  onClose,
  onSaved,
}: {
  trialId: number;
  criterion?: TrialCriterionDto;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CriterionFormInput>({
    resolver: zodResolver(CriterionFormSchema),
    defaultValues: criterion
      ? {
          content: criterion.content,
          category: criterion.category,
          appliesMale: criterion.appliesMale,
          appliesFemale: criterion.appliesFemale,
        }
      : EMPTY_CRITERION_FORM,
  });

  const onSubmit = async (data: CriterionFormInput): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    const payload = {
      content: data.content,
      category: data.category,
      appliesMale: data.appliesMale,
      appliesFemale: data.appliesFemale,
    };
    try {
      if (criterion) {
        await trialsApi.updateCriterion(trialId, criterion.id, payload);
      } else {
        await trialsApi.createCriterion(trialId, payload);
      }
      onSaved();
    } catch (err: unknown) {
      setErrorMessage(
        await getApiErrorMessage(err, 'Nie udało się zapisać kryterium.'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const dialogTitle = criterion ? 'Edycja kryterium' : 'Dodaj kryterium';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="criterion-form-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="criterion-form-title" className="text-base font-semibold text-slate-100">
              {dialogTitle}
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Kryterium musi dotyczyć co najmniej jednej płci.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label htmlFor="criterion-content" className={labelClass}>
              Treść
            </label>
            <input
              id="criterion-content"
              type="text"
              disabled={isSubmitting}
              className={fieldClass}
              {...register('content')}
            />
            {errors.content && (
              <p className="mt-1 text-xs text-rose-400">{errors.content.message}</p>
            )}
          </div>

          <div>
            <span className={labelClass}>Kategoria</span>
            <div className="mt-2 flex items-center gap-5">
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="radio"
                  value={CRITERION_CATEGORIES.INCLUSION}
                  disabled={isSubmitting}
                  {...register('category')}
                />
                Włączenia
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="radio"
                  value={CRITERION_CATEGORIES.EXCLUSION}
                  disabled={isSubmitting}
                  {...register('category')}
                />
                Wyłączenia
              </label>
            </div>
          </div>

          <div>
            <span className={labelClass}>Dotyczy płci</span>
            <div className="mt-2 flex items-center gap-5">
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input type="checkbox" disabled={isSubmitting} {...register('appliesMale')} />
                Mężczyźni
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input type="checkbox" disabled={isSubmitting} {...register('appliesFemale')} />
                Kobiety
              </label>
            </div>
            {errors.appliesMale && (
              <p className="mt-1 text-xs text-rose-400">{errors.appliesMale.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Zapisywanie…' : criterion ? 'Zapisz zmiany' : 'Zapisz kryterium'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CriterionAnswersDialog({
  trialId,
  criterion,
  answers,
  onClose,
}: {
  trialId: number;
  criterion: TrialCriterionDto;
  answers: TrialCriterionAnswerDto[];
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [answerText, setAnswerText] = useState('');
  const [answerMet, setAnswerMet] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.criteria(trialId) });
  };

  const addMutation = useMutation({
    mutationFn: (request: AddCriterionAnswerRequest) =>
      trialsApi.addCriterionAnswer(trialId, criterion.id, request),
    onSuccess: invalidate,
  });
  const removeMutation = useMutation({
    mutationFn: (answerId: number) =>
      trialsApi.deleteCriterionAnswer(trialId, criterion.id, answerId),
    onSuccess: invalidate,
  });

  const submitAnswer = async (): Promise<void> => {
    const trimmed = answerText.trim();
    if (trimmed.length === 0) {
      setValidationError('Treść odpowiedzi jest wymagana');
      return;
    }
    if (trimmed.length > 255) {
      setValidationError('Treść odpowiedzi może mieć maksymalnie 255 znaków');
      return;
    }
    setValidationError(null);
    setErrorMessage(null);
    try {
      await addMutation.mutateAsync({ answer: trimmed, met: answerMet });
      setAnswerText('');
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się dodać odpowiedzi.'));
    }
  };

  const removeAnswer = async (answerId: number): Promise<void> => {
    setErrorMessage(null);
    try {
      await removeMutation.mutateAsync(answerId);
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się usunąć odpowiedzi.'));
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="criterion-answers-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="criterion-answers-title" className="text-base font-semibold text-slate-100">
              Odpowiedzi kryterium
            </h2>
            <p className="mt-1 text-xs text-slate-400">{criterion.content}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {errorMessage && (
          <div
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="mt-4 overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/60">
                <th scope="col" className="px-3 py-2 text-slate-400">
                  Odpowiedź
                </th>
                <th scope="col" className="px-3 py-2 text-slate-400">
                  Stan
                </th>
                <th scope="col" className="px-3 py-2 text-slate-400">
                  Działania
                </th>
              </tr>
            </thead>
            <tbody>
              {answers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-slate-500">
                    Brak zdefiniowanych odpowiedzi.
                  </td>
                </tr>
              ) : (
                answers.map((answer) => (
                  <tr key={answer.id} className="border-b border-slate-800/80">
                    <td className="px-3 py-2 text-slate-200">{answer.answer}</td>
                    <td className="px-3 py-2 text-slate-200">{formatAnswerState(answer.met)}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => void removeAnswer(answer.id)}
                        disabled={removeMutation.isPending}
                        className={actionButtonClass}
                      >
                        Usuń odpowiedź
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-5 border-t border-slate-800 pt-4">
          <div>
            <label htmlFor="answer-text" className={labelClass}>
              Treść odpowiedzi
            </label>
            <input
              id="answer-text"
              type="text"
              disabled={addMutation.isPending}
              className={fieldClass}
              value={answerText}
              onChange={(event) => setAnswerText(event.target.value)}
            />
            {validationError && <p className="mt-1 text-xs text-rose-400">{validationError}</p>}
          </div>
          <div className="mt-3 flex items-center gap-5">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="radio"
                name="answer-met"
                checked={answerMet}
                onChange={() => setAnswerMet(true)}
              />
              Spełnione
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="radio"
                name="answer-met"
                checked={!answerMet}
                onChange={() => setAnswerMet(false)}
              />
              Niespełnione
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => void submitAnswer()}
              disabled={addMutation.isPending}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              Dodaj odpowiedź
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteCriterionDialog({
  trialId,
  criterion,
  onClose,
}: {
  trialId: number;
  criterion: TrialCriterionDto;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => trialsApi.deleteCriterion(trialId, criterion.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trialQueryKeys.criteria(trialId) });
      onClose();
    },
  });

  const confirm = async (): Promise<void> => {
    setErrorMessage(null);
    try {
      await deleteMutation.mutateAsync();
    } catch (err: unknown) {
      // Gate 6: 409 while patients have saved answers — the dialog stays open
      // with the server block message and the row remains in the table.
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się usunąć kryterium.'));
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-criterion-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <h2 id="delete-criterion-title" className="text-base font-semibold text-slate-100">
          Usunięcie kryterium
        </h2>
        <p className="mt-3 text-sm text-slate-300">
          Usunąć kryterium „{criterion.content}”?
        </p>
        <p className="mt-1 text-xs text-slate-400">
          Zapisane odpowiedzi pacjentów pozostają kopią tekstową i nie są zmieniane.
        </p>

        {errorMessage && (
          <div
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2 border-t border-slate-800 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={deleteMutation.isPending}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={deleteMutation.isPending}
            className="rounded-md bg-rose-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-rose-500 disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Usuwanie…' : 'Usuń'}
          </button>
        </div>
      </div>
    </div>
  );
}
